import type { SolMetadata } from '@seek/sol-js';
import React, { useEffect } from 'react';

import {
  MINIMUM_MATCHES_TO_DISPLAY,
  type useJDVSkillsMatchProps,
} from 'src/components/JobDetailsPage/useJDVSkillsMatchProps';
import type { Skill } from 'src/graphql/graphql';
import {
  isBrowserAnalyticsFacade,
  useAnalyticsFacade,
} from 'src/modules/AnalyticsFacade';
import { selectFeatureFlag } from 'src/store/featureFlags/selectors';
import { useSelector } from 'src/store/react';

const currentPageSection = 'skills match';

const getMatchedSkillsVisibilityStatus = (skillsCount: number) => {
  if (skillsCount >= MINIMUM_MATCHES_TO_DISPLAY) {
    return 'VISIBLE: NO ERROR';
  }
  if (skillsCount > 0) {
    return 'HIDDEN - LESS THAN 3 SKILLS';
  }
  return 'HIDDEN - UNMATCHED';
};

const getQualitiesText = (quality: Skill) => quality.keyword.text;
const getShownQualities = ({
  matched,
  inferred,
  unmatched,
}: {
  matched?: Skill[];
  inferred?: Skill[];
  unmatched?: Skill[];
}) => ({
  matchedQualitiesShown: matched?.map(getQualitiesText),
  inferredQualitiesShown: inferred?.map(getQualitiesText),
  unmatchedQualitiesShown: unmatched?.map(getQualitiesText),
});

export const useSkillsMatchTracking = ({
  qualities,
  jobId,
  solMetadata,
}: {
  qualities: ReturnType<typeof useJDVSkillsMatchProps>;
  jobId: string;
  solMetadata: SolMetadata;
}) => {
  const enableMatchedQualities = useSelector(
    selectFeatureFlag('matchedQualities'),
  );

  const analyticsFacade = useAnalyticsFacade();
  const {
    isLoadingQualities,
    shouldShowSkillsMatch,
    matchedQualitiesRequestToken,
    skillsCount,
    error,
  } = qualities ?? { isLoadingQualities: null, shouldShowSkillsMatch: null };
  const [isTrackingSent, setIsTrackingSent] = React.useState(false);

  useEffect(() => {
    if (!enableMatchedQualities || isTrackingSent || isLoadingQualities) {
      return;
    }
    if (error) {
      analyticsFacade.skillsMatchLoadFailed({
        currentPageSection,
        isLoggedIn: true,
        matchedSkillsVisibilityStatus:
          'network error: trouble loading matched skills',
      });
    } else if (isBrowserAnalyticsFacade(analyticsFacade)) {
      const matchedSkillsVisibilityStatus =
        getMatchedSkillsVisibilityStatus(skillsCount);
      const {
        matchedQualitiesShown,
        inferredQualitiesShown,
        unmatchedQualitiesShown,
      } = getShownQualities({
        matched: qualities?.matched,
        inferred: qualities?.inferred,
        unmatched: qualities?.unmatched,
      });
      analyticsFacade.skillsMatchLoadSucceeded(
        {
          currentPageSection,
          isLoggedIn: true,
          areMatchedSkillsVisible: shouldShowSkillsMatch!,
          matchedSkillsVisibilityStatus,
        },
        {
          jobId,
          matchedQualitiesShown,
          inferredQualitiesShown,
          unmatchedQualitiesShown,
          matchedQualitiesRequestToken: matchedQualitiesRequestToken ?? 'NULL',
          matchedSkillsVisibilityStatus,
          solMetadata,
        },
      );
    }
    setIsTrackingSent(true);
  }, [
    analyticsFacade,
    error,
    isLoadingQualities,
    isTrackingSent,
    shouldShowSkillsMatch,
    skillsCount,
    enableMatchedQualities,
    qualities?.matched,
    qualities?.inferred,
    qualities?.unmatched,
    jobId,
    matchedQualitiesRequestToken,
    solMetadata,
  ]);
};
